import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Notifications raised before the component mounts wait here
const pendingNotifications = [];
let enqueueNotification = null;

// Global function to show notifications
export const showNotification = (type, message, duration = 3000) => {
  const notification = {
    id: `${Date.now()}-${Math.random()}`,
    type,
    message,
    duration
  };

  if (enqueueNotification) {
    enqueueNotification(notification);
  } else {
    pendingNotifications.push(notification);
  }
};

/**
 * Solid colour rather than the pale tint this used to have.
 *
 * A till is used in daylight, often on a cheap screen at arm's length. A
 * green-50 background with green-800 text is close to unreadable in those
 * conditions, and "payment recorded" is exactly the message that must not be
 * missed. White on a saturated ground carries.
 */
const TONES = {
  success: { bg: 'bg-green-600', ring: 'ring-green-700/20', Icon: CheckCircle2 },
  error: { bg: 'bg-red-600', ring: 'ring-red-700/20', Icon: AlertCircle },
  warning: { bg: 'bg-amber-500', ring: 'ring-amber-600/20', Icon: AlertTriangle },
  info: { bg: 'bg-gray-900', ring: 'ring-black/20', Icon: Info }
};

/** How long the slide-out runs. Must match the duration class below. */
const EXIT_MS = 200;

function Toast({ notification, onDismiss }) {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const tone = TONES[notification.type] || TONES.info;
  const { Icon } = tone;

  const close = useCallback(() => {
    setVisible(false);
    // Let the slide-out finish before the node is pulled from the tree,
    // otherwise it vanishes instantly and the animation is never seen.
    setTimeout(() => onDismiss(notification.id), EXIT_MS);
  }, [notification.id, onDismiss]);

  // Mount, then flip to the visible state on the next frame. Setting both in one
  // pass would give the browser no start state to transition from, which is why
  // the previous version never actually animated.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (notification.duration <= 0) return undefined;
    const timer = setTimeout(close, notification.duration);
    return () => clearTimeout(timer);
  }, [notification.duration, close]);

  return (
    <div
      // Errors interrupt a screen reader; everything else waits for a pause.
      role={notification.type === 'error' ? 'alert' : 'status'}
      aria-live={notification.type === 'error' ? 'assertive' : 'polite'}
      className={`pointer-events-auto w-full transform transition-all duration-200 ease-out ${
        visible ? 'translate-y-0 opacity-100 scale-100' : '-translate-y-2 opacity-0 scale-95'
      }`}
    >
      <div
        className={`${tone.bg} ${tone.ring} text-white rounded-xl shadow-lg ring-1 flex items-center gap-3 pl-3 pr-2 py-2.5`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <p className="flex-1 text-sm font-medium leading-snug">{notification.message}</p>
        <button
          onClick={close}
          aria-label={t('Dismiss')}
          className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

const GlobalNotification = () => {
  // A queue rather than a single value, so a burst of notifications is shown
  // one after another instead of each overwriting the last
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    enqueueNotification = (next) => setQueue((current) => [...current, next]);

    // Drain anything raised before we mounted
    if (pendingNotifications.length > 0) {
      setQueue((current) => [...current, ...pendingNotifications.splice(0)]);
    }

    return () => {
      enqueueNotification = null;
    };
  }, []);

  const dismiss = useCallback((id) => {
    setQueue((current) => current.filter((item) => item.id !== id));
  }, []);

  if (queue.length === 0) return null;

  return (
    /*
      Centred and width-capped rather than pinned to the right. At `right-4` with
      `w-full max-w-sm` a long message on a narrow phone ran to the screen edge;
      this keeps an even margin on both sides at every width.

      Sits below the sticky header (h-16) so it never covers the tenant name or
      the menu button, and is click-through except on the toasts themselves.
    */
    <div className="fixed inset-x-0 top-[4.5rem] z-[9999] flex flex-col items-center gap-2 px-3 pointer-events-none">
      {/* Three is enough to see a burst without burying the screen in them. */}
      {queue.slice(0, 3).map((notification) => (
        <div key={notification.id} className="w-full max-w-sm">
          <Toast notification={notification} onDismiss={dismiss} />
        </div>
      ))}
    </div>
  );
};

export default GlobalNotification;
